// The project ASSET ORIGIN shim — runtime resolution of project-file references
// for EVERY Creative Code Studio runtime (website, Phaser, three.js).
//
// Why it exists (owner bug report 2026-09-22): a website's `/api/...` route
// returned rows whose picture column held `assets/imported/celebi.jpg`; the page
// set `img.src = row.image` and the browser fetched
// `https://app.airbotix.ai/assets/imported/celebi.jpg`. The studio's build-time
// inliner (`inlineAssetRefs`) only rewrites references that are written
// LITERALLY in a project file; a reference that first exists at run time — a db
// row, an `/api/` response, a `data/*.json` entry, a computed string — never
// passes through it. Inside an opaque-origin `srcdoc` frame every relative URL
// resolves against the PARENT studio page, which has no such file.
//
// The same class hits games: a level file naming a sprite, an atlas JSON naming
// its image, a computed texture path. So the fix is ONE engine-agnostic shim,
// injected by all three builders BEFORE the engine and before any kid script,
// that carries the project asset map and resolves in layers:
//
//   1. DOM      — `src` / `poster` property setters on image, media and source
//                 elements, `Element.prototype.setAttribute`, and a
//                 MutationObserver for markup inserted via innerHTML (attributes
//                 AND inline `style="… url(…)"`, plus dynamically added
//                 `<style>` text).
//   2. Network  — `fetch` and `XMLHttpRequest.prototype.open`: a project path
//                 returns the inlined bytes (covers Phaser's JSON/audio/atlas
//                 loads and three.js `FileLoader`).
//   3. CSS-in-JS — `style.backgroundImage` / `background` / `cssText` setters
//                 and `setProperty`.
//   4. Explicit — `window.assets.url(path)` for anything the layers above cannot
//                 see; `assets.has(path)` / `assets.list()` for discovery.
//   5. Evidence — a project-shaped reference that matches NO project file is
//                 named in a kid-readable console.error (once per path) AND posted
//                 as an `{ __airbotixAsset, path, missing: true }` outcome so the
//                 RunReport carries it (`missing-ref`) and a fix turn sees it.
//                 A resolved reference posts `{ path, ok }` from the element's
//                 load / error event (or immediately for fetch / XHR).
//
// Matching reuses the build-time rules (`normalizeAssetRef`): `./`, `/`, `%20`,
// `?v=` / `#frag`, and letter case when it names exactly one asset. Absolute URLs
// (`https:`, `data:`, `blob:`, `//cdn…`) are never a project file.
//
// The build-time inliner stays: static markup must never fire a request at the
// studio origin before the observer runs. The map therefore carries a second copy
// of each asset's bytes (the first lives wherever the inliner rewrote it). A
// dedicated preview origin (learn-code-studio-prd §5.2) would retire both.
//
// The shim is ALWAYS injected (also for a project with no assets) so
// `assets.url()` exists on every runtime the agent prompt describes.

import type { VfsFile } from './codeApi';
import { ASSET_MIME } from './buildPreview';

/** A VFS asset's content as a ready `data:` URL (the inliner and the shim agree). */
export function toDataUrl(asset: VfsFile): string {
  if (asset.content.startsWith('data:')) return asset.content;
  const ext = asset.path.split('.').pop()?.toLowerCase() ?? '';
  return `data:${ASSET_MIME[ext] ?? 'application/octet-stream'};base64,${asset.content}`;
}

/** The kid-readable line for one media reference that matches no project file.
 *  ONE wording, used at build time (site builder scan) and at run time (this shim). */
export function missingMediaMessage(ref: string): string {
  const clean = ref.replace(/[?#].*$/, '');
  const noun = /\.(mp3|wav|ogg|m4a|mp4|webm)$/i.test(clean)
    ? 'sound or video'
    : /\.(ttf|otf|woff2?)$/i.test(clean)
      ? 'font'
      : /\.(glb|gltf)$/i.test(clean)
        ? '3D model'
        : /\.(json|xml|csv|txt|atlas|fnt)$/i.test(clean)
          ? 'data file'
          : 'picture';
  return (
    `Missing ${noun} "${ref}" — no file with that exact name is in your project. ` +
    `Check My assets and use the file's exact path (like assets/imported/cat.png). ` +
    `A picture from the web works too — use its full https:// link.`
  );
}

/** Embed a string safely inside injected `<script>` source. */
const jsStr = (s: string): string =>
  JSON.stringify(s).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');

/** The global the runtime exposes for the site runtime / engine guards to call. */
export const ASSET_ORIGIN_GLOBAL = '__airbotixAssetOrigin';

/**
 * Build the asset-origin `<script>` for one project. `assets` are the VFS assets
 * plus any virtual class-shared assets (both already inlined the same way by the
 * builders). Pure: same inputs → the same tag.
 */
export function buildAssetOriginShim(assets: VfsFile[]): string {
  const map: Record<string, string> = {};
  for (const a of assets) map[a.path] = toDataUrl(a);
  const mapJson = jsStr(JSON.stringify(map));
  return `<script>
(function () {
  var MAP = JSON.parse(${mapJson});
  var LOWER = {};
  var PATHS = [];
  for (var p in MAP) {
    PATHS.push(p);
    var lk = p.toLowerCase();
    LOWER[lk] = Object.prototype.hasOwnProperty.call(LOWER, lk) ? false : p;
  }
  var NON_PROJECT = /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:|\\/\\/|#)/;
  var MEDIA = /\\.(png|jpe?g|gif|webp|svg|avif|bmp|mp3|wav|ogg|m4a|mp4|webm|glb|gltf|json|xml|csv|txt|atlas|fnt|ttf|otf|woff2?)$/i;

  function normalize(ref) {
    if (typeof ref !== 'string') return null;
    var v = ref.replace(/^\\s+|\\s+$/g, '');
    if (v.length === 0 || v.length > 512 || /[\\n\\r]/.test(v) || NON_PROJECT.test(v)) return null;
    v = v.replace(/[?#].*$/, '');
    while (v.slice(0, 2) === './') v = v.slice(2);
    while (v.charAt(0) === '/') v = v.slice(1);
    try { v = decodeURIComponent(v); } catch (e) {}
    return v.length > 0 ? v : null;
  }
  function resolve(ref) {
    var key = normalize(ref);
    if (key === null) return null;
    if (Object.prototype.hasOwnProperty.call(MAP, key)) return MAP[key];
    var loose = LOWER[key.toLowerCase()];
    return typeof loose === 'string' ? MAP[loose] : null;
  }
  function projectShaped(ref) {
    var key = normalize(ref);
    return key !== null && (key.slice(0, 7) === 'assets/' || MEDIA.test(key));
  }
  function missingMessage(ref) {
    var clean = ref.replace(/[?#].*$/, '');
    var noun = /\\.(mp3|wav|ogg|m4a|mp4|webm)$/i.test(clean) ? 'sound or video'
      : /\\.(ttf|otf|woff2?)$/i.test(clean) ? 'font'
      : /\\.(glb|gltf)$/i.test(clean) ? '3D model'
      : /\\.(json|xml|csv|txt|atlas|fnt)$/i.test(clean) ? 'data file'
      : 'picture';
    return 'Missing ' + noun + ' "' + ref + '" \\u2014 no file with that exact name is in your project. ' +
      'Check My assets and use the file\\'s exact path (like assets/imported/cat.png). ' +
      'A picture from the web works too \\u2014 use its full https:// link.';
  }

  // ── evidence: one outcome per path, never a flood ──────────────────────────
  var posted = {};
  function post(path, ok, missing, error) {
    try {
      var key = String(path).slice(0, 200);
      if (posted[key]) return;
      posted[key] = true;
      var m = { __airbotixAsset: true, path: key, ok: ok };
      if (missing) m.missing = true;
      if (error) m.error = String(error).slice(0, 200);
      parent.postMessage(m, '*');
    } catch (e) {}
  }
  function pathOf(ref) {
    var key = normalize(ref);
    if (key === null) return String(ref);
    if (Object.prototype.hasOwnProperty.call(MAP, key)) return key;
    var loose = LOWER[key.toLowerCase()];
    return typeof loose === 'string' ? loose : key;
  }
  var warned = {};
  // Resolve, or — for a reference that LOOKS like a project file but matches
  // none — say so once, kid-readably, and record it for the run report.
  function resolveOrWarn(ref) {
    var hit = resolve(ref);
    if (hit) return hit;
    if (projectShaped(ref)) {
      var key = normalize(ref);
      if (!warned[key]) {
        warned[key] = true;
        try { console.error(missingMessage(String(ref).replace(/^\\s+|\\s+$/g, ''))); } catch (e) {}
        post(key, false, true, 'not in the project');
      }
    }
    return null;
  }
  function watch(el, ref) {
    try {
      if (!el || el.__airbotixWatched) return;
      el.__airbotixWatched = true;
      var path = pathOf(ref);
      var onLoad = function () { post(path, true); };
      var onError = function () { post(path, false, false, 'failed to load'); };
      el.addEventListener('load', onLoad);
      el.addEventListener('loadeddata', onLoad);
      el.addEventListener('error', onError);
    } catch (e) {}
  }

  // ── CSS url(…) rewriting (inline style attributes, <style> text, CSS-in-JS) ─
  var CSS_URL = /url\\(\\s*(["']?)([^"')]+)\\1\\s*\\)/g;
  function rewriteCss(text) {
    if (typeof text !== 'string' || text.indexOf('url(') === -1) return text;
    return text.replace(CSS_URL, function (whole, q, ref) {
      var hit = resolveOrWarn(ref);
      return hit ? 'url("' + hit + '")' : whole;
    });
  }

  // ── layer 1: DOM property setters + setAttribute ────────────────────────────
  function isMediaEl(el) {
    var t = el && el.tagName ? el.tagName.toUpperCase() : '';
    return t === 'IMG' || t === 'VIDEO' || t === 'AUDIO' || t === 'SOURCE';
  }
  function patchAccessor(proto, prop) {
    try {
      var d = Object.getOwnPropertyDescriptor(proto, prop);
      if (!d || typeof d.set !== 'function') return;
      Object.defineProperty(proto, prop, {
        configurable: true,
        enumerable: d.enumerable,
        get: d.get,
        set: function (v) {
          var hit = resolveOrWarn(v);
          if (hit) watch(this, v);
          return d.set.call(this, hit || v);
        }
      });
    } catch (e) {}
  }
  if (window.HTMLImageElement) patchAccessor(HTMLImageElement.prototype, 'src');
  if (window.HTMLMediaElement) patchAccessor(HTMLMediaElement.prototype, 'src');
  if (window.HTMLSourceElement) patchAccessor(HTMLSourceElement.prototype, 'src');
  if (window.HTMLVideoElement) patchAccessor(HTMLVideoElement.prototype, 'poster');
  try {
    var setAttr = Element.prototype.setAttribute;
    Element.prototype.setAttribute = function (name, value) {
      try {
        var n = String(name).toLowerCase();
        if ((n === 'src' || n === 'poster') && isMediaEl(this)) {
          var hit = resolveOrWarn(value);
          if (hit) { watch(this, value); return setAttr.call(this, name, hit); }
        } else if (n === 'style') {
          return setAttr.call(this, name, rewriteCss(String(value)));
        }
      } catch (e) {}
      return setAttr.call(this, name, value);
    };
  } catch (e) {}

  // ── layer 1b: markup inserted at run time (innerHTML, insertAdjacentHTML…) ─
  function fixEl(el) {
    try {
      if (!el || el.nodeType !== 1) return;
      var tag = el.tagName.toUpperCase();
      if (tag === 'STYLE') {
        var css = el.textContent;
        var out = rewriteCss(css);
        if (out !== css) el.textContent = out;
        return;
      }
      if (isMediaEl(el)) {
        var attrs = tag === 'VIDEO' ? ['src', 'poster'] : ['src'];
        for (var i = 0; i < attrs.length; i++) {
          var v = el.getAttribute(attrs[i]);
          if (v == null) continue;
          var hit = resolveOrWarn(v);
          if (hit) { watch(el, v); el.setAttribute(attrs[i], hit); }
        }
      }
      var style = el.getAttribute('style');
      if (style && style.indexOf('url(') !== -1) {
        var fixed = rewriteCss(style);
        if (fixed !== style) el.setAttribute('style', fixed);
      }
    } catch (e) {}
  }
  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    fixEl(node);
    if (!node.querySelectorAll) return;
    // [style] plus an in-code url( check: an attribute selector carrying a
    // parenthesis is not matched reliably by every selector engine.
    var els = node.querySelectorAll('img,video,audio,source,style,[style]');
    for (var i = 0; i < els.length; i++) fixEl(els[i]);
  }
  try {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) scan(m.addedNodes[j]);
        } else if (m.type === 'attributes') {
          fixEl(m.target);
        }
      }
    }).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'poster', 'style']
    });
  } catch (e) {}

  // ── layer 2: network — fetch / XMLHttpRequest of a project path ────────────
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch : null;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && typeof input.url === 'string' ? input.url : null);
        var hit = url === null ? null : resolveOrWarn(url);
        if (hit) { post(pathOf(url), true); return nativeFetch.call(window, hit, init); }
      } catch (e) {}
      return nativeFetch.call(window, input, init);
    };
  }
  try {
    var xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        var hit = resolveOrWarn(url);
        if (hit) {
          post(pathOf(url), true);
          var args = Array.prototype.slice.call(arguments);
          args[1] = hit;
          return xhrOpen.apply(this, args);
        }
      } catch (e) {}
      return xhrOpen.apply(this, arguments);
    };
  } catch (e) {}

  // ── layer 3: CSS-in-JS (el.style.backgroundImage = "url(assets/…)") ────────
  if (window.CSSStyleDeclaration) {
    var cssProps = ['background', 'backgroundImage', 'cssText', 'borderImage', 'listStyleImage', 'content'];
    for (var ci = 0; ci < cssProps.length; ci++) {
      (function (prop) {
        try {
          var d = Object.getOwnPropertyDescriptor(CSSStyleDeclaration.prototype, prop);
          if (!d || typeof d.set !== 'function') return;
          Object.defineProperty(CSSStyleDeclaration.prototype, prop, {
            configurable: true, enumerable: d.enumerable, get: d.get,
            set: function (v) { return d.set.call(this, rewriteCss(v)); }
          });
        } catch (e) {}
      })(cssProps[ci]);
    }
    try {
      var setProp = CSSStyleDeclaration.prototype.setProperty;
      CSSStyleDeclaration.prototype.setProperty = function (name, value, priority) {
        return setProp.call(this, name, rewriteCss(value), priority);
      };
    } catch (e) {}
  }

  // ── layer 4: the explicit helper + the internal hook for other shims ───────
  window.assets = {
    url: function (path) { var hit = resolveOrWarn(path); return hit ? hit : String(path); },
    has: function (path) { return resolve(path) !== null; },
    list: function () { return PATHS.slice(); }
  };
  window.${ASSET_ORIGIN_GLOBAL} = {
    resolve: resolve,
    resolveOrWarn: resolveOrWarn,
    fetch: nativeFetch ? function (input, init) { return nativeFetch.call(window, input, init); } : null
  };
})();
</script>`;
}
